import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../scss/calendar.scss';
import { Card, Spinner, Alert } from '@themesberg/react-bootstrap';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { decodeSpecialCharacters, formatDescription } from '../utils/textUtils';

// Configurar moment en español
moment.locale('es', {
  months: 'Enero_Febrero_Marzo_Abril_Mayo_Junio_Julio_Agosto_Septiembre_Octubre_Noviembre_Diciembre'.split('_'),
  monthsShort: 'Ene_Feb_Mar_Abr_May_Jun_Jul_Ago_Sep_Oct_Nov_Dic'.split('_'),
  weekdays: 'Domingo_Lunes_Martes_Miércoles_Jueves_Viernes_Sábado'.split('_'),
  weekdaysShort: 'Dom_Lun_Mar_Mié_Jue_Vie_Sáb'.split('_'),
  weekdaysMin: 'Do_Lu_Ma_Mi_Ju_Vi_Sa'.split('_')
});

const localizer = momentLocalizer(moment);

const AttendanceCalendar = ({ onEventSelect }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthRows, setMonthRows] = useState(5);

  // Función para calcular el número de filas del mes
  const calculateMonthRows = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const firstDayOfWeek = firstDay.getDay(); // 0 = domingo
    const daysInMonth = lastDay.getDate();
    
    // Calcular cuántas filas necesitamos
    const totalCells = firstDayOfWeek + daysInMonth;
    const rows = Math.ceil(totalCells / 7);
    
    return rows;
  };

  // Actualizar filas cuando cambia la fecha
  useEffect(() => {
    if (currentView === 'month') {
      const rows = calculateMonthRows(currentDate);
      setMonthRows(rows);
    }
  }, [currentDate, currentView]);

  useEffect(() => {
    loadEvents();
  }, [user]);

  const loadEvents = async () => {
    if (!user?.email) {
      setError('Usuario no autenticado');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/workspace/user-events/?email=${user.email}&page=1&limit=10`);
      const responseData = await response.json();
      const eventsData = responseData.data || responseData || [];
      
      // Convertir eventos del API al formato del calendario
      const calendarEvents = eventsData.map((event, index) => {

        
        return {
        id: event.id || index,
        title: decodeSpecialCharacters(event.summary || 'Evento sin título'),
        start: new Date(event.start?.dateTime || event.start?.date),
        end: new Date(event.end?.dateTime || event.end?.date),
        organizer_name: event.organizer?.email || '',
        resource: { 
          type: 'evento', 
          description: formatDescription(decodeSpecialCharacters(event.description || '')),
          location: decodeSpecialCharacters(event.location || ''),
        }
      };
      });
      

      
      setEvents(calendarEvents);
    } catch (err) {
      console.error('Error al cargar eventos:', err);
      setError('Error al cargar eventos del calendario');
    } finally {
      setLoading(false);
    }
  };

  // Personalizar colores según el tipo de evento
  const eventStyleGetter = (event) => {
    let backgroundColor = '#007bff'; // Azul por defecto
    
    // Puedes personalizar colores según propiedades del evento
    if (event.resource?.type === 'evento') {
      backgroundColor = '#28a745'; // Verde para eventos
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const handleSelectEvent = (event) => {
    if (onEventSelect) {
      onEventSelect(event);
    } else {
      // Fallback al alert original si no se proporciona onEventSelect
      const details = [
        `Evento: ${event.title}`,
        `Fecha: ${moment(event.start).format('DD/MM/YYYY HH:mm')}`,
        event.resource?.location ? `Ubicación: ${event.resource.location}` : '',
        event.resource?.description ? `Descripción: ${event.resource.description}` : ''
      ].filter(Boolean).join('\n');
      
      alert(details);
    }
  };

  const handleNavigate = (date, view, action) => {
    setCurrentDate(date);
    
    // Recalcular filas si estamos en vista mes
    if (view === 'month' || currentView === 'month') {
      const rows = calculateMonthRows(date);
      setMonthRows(rows);
    }
    
    // Si es el botón "Hoy", cambiar a vista día
    if (action === 'TODAY') {
      setCurrentView('day');
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    
    // Recalcular filas si cambiamos a vista mes
    if (view === 'month') {
      const rows = calculateMonthRows(currentDate);
      setMonthRows(rows);
    }
  };

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">📅 Calendario de Eventos</h5>
        </Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" className="me-2" />
          Cargando eventos...
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">📅 Calendario de Eventos</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">📅 Calendario de Eventos - {decodeSpecialCharacters(user?.name || user?.displayName || user?.given_name || '') || user?.email}</h5>
      </Card.Header>
      <Card.Body>
        <div 
          style={{ 
            height: currentView === 'month' 
              ? (window.innerWidth <= 768 
                  ? `${280 + (monthRows * 60)}px`
                  : `${350 + (monthRows * 85)}px`)
              : (window.innerWidth <= 768 ? '500px' : '650px'),
            '--month-rows': monthRows
          }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            date={currentDate}
            view={currentView}
            views={['month', 'week', 'day', 'agenda']}
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              week: "Semana",
              day: "Día",
              agenda: "Agenda",
              date: "Fecha",
              time: "Hora",
              event: "Evento",
              noEventsInRange: "No hay eventos en este rango",
              showMore: total => `+ Ver más (${total})`
            }}
          />
        </div>
        
        {/* Info */}
        <div className="mt-3">
          <small className="text-muted">
            Total de eventos: {events.length}
            <span className="badge bg-success ms-2">Eventos de Google Calendar</span>
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AttendanceCalendar;